import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/GlobalStyles';

type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
type HeadingVariant = 'default' | 'primary' | 'secondary';

interface HeadingProps {
  level?: HeadingLevel;
  variant?: HeadingVariant;
  align?: 'left' | 'center' | 'right';
}

const getVariantStyles = (variant: HeadingVariant) => {
  switch (variant) {
    case 'primary':
      return `
        color: ${theme.colors.primary};
      `;
    case 'secondary':
      return `
        color: ${theme.colors.secondary};
      `;
    default:
      return `
        color: ${theme.colors.text};
      `;
  }
};

const getLevelStyles = (level: HeadingLevel) => {
  switch (level) {
    case 'h1':
      return `
        font-size: ${theme.typography.h1.fontSize};
        font-weight: ${theme.typography.h1.fontWeight};
        line-height: ${theme.typography.h1.lineHeight};
        letter-spacing: ${theme.typography.h1.letterSpacing};
        margin-bottom: ${theme.spacing.xl};
      `;
    case 'h2':
      return `
        font-size: ${theme.typography.h2.fontSize};
        font-weight: ${theme.typography.h2.fontWeight};
        line-height: ${theme.typography.h2.lineHeight};
        letter-spacing: ${theme.typography.h2.letterSpacing};
        margin-bottom: ${theme.spacing.lg};
      `;
    case 'h3':
      return `
        font-size: ${theme.typography.h3.fontSize};
        font-weight: ${theme.typography.h3.fontWeight};
        line-height: ${theme.typography.h3.lineHeight};
        margin-bottom: ${theme.spacing.lg};
      `;
    case 'h4':
      return `
        font-size: clamp(1.25rem, 2.5vw, 1.5rem);
        font-weight: 600;
        line-height: 1.4;
        margin-bottom: ${theme.spacing.md};
      `;
    case 'h5':
      return `
        font-size: 1.125rem;
        font-weight: 600;
        line-height: 1.4;
        margin-bottom: ${theme.spacing.sm};
      `;
    case 'h6':
      return `
        font-size: 1rem;
        font-weight: 600;
        line-height: 1.4;
        margin-bottom: ${theme.spacing.sm};
      `;
    default:
      return '';
  }
};

const StyledHeading = styled.h1<HeadingProps>`
  font-family: ${theme.fonts.secondary};
  font-weight: 700;
  text-align: ${({ align = 'left' }) => align};

  ${({ level = 'h1' }) => getLevelStyles(level)}
  ${({ variant = 'default' }) => getVariantStyles(variant)}
`;

export const Heading: React.FC<HeadingProps & React.HTMLAttributes<HTMLHeadingElement>> = ({
  level = 'h1',
  variant = 'default',
  align = 'left',
  children,
  ...props
}) => {
  return (
    <StyledHeading as={level} level={level} variant={variant} align={align} {...props}>
      {children}
    </StyledHeading>
  );
}; 